import { designVariables } from "@studio3t/design-variables";
import { ButtonSmallSubtleIcon, type Icon } from "@studio3t/generic-components";
import styled from "styled-components";
import { OverlayScroll } from "@/components/OverlayScroll";
import { useLabeledButton } from "./PipelineNavBar";

// Figma Bridge_DocPreview (14662-144779): a 366x466 card clipping a
// read-only content view — cards1 chrome, 18px corner, the design's
// two-layer elevation shadow. Shown next to a previewable tree leaf after
// a long hover (Bridge_Explorer 14662-134737). The shell is shared by the
// code previews (SchemaPreview) and the test preview (TestPreview).

export const PREVIEW_WIDTH = 366;
export const PREVIEW_MAX_HEIGHT = 466;

// Marks the card in the DOM so the explorer's scroll guard can tell the
// card's own scrolling (which must keep it open) from tree scrolling
// (which closes it) — the portaled card is still a React child of the
// tree panel, so its scroll events reach the panel's capture handler.
export const PREVIEW_CARD_ATTR = "data-preview-card";

export const PreviewCard = styled.div.attrs(
  // data-* attributes aren't in the div's attr typing — assert past it.
  { [PREVIEW_CARD_ATTR]: "" } as React.HTMLAttributes<HTMLDivElement>,
)`
  display: flex;
  flex-direction: column;
  width: ${PREVIEW_WIDTH}px;
  max-width: calc(100vw - 16px);
  max-height: ${PREVIEW_MAX_HEIGHT}px;
  overflow: hidden;
  background: ${(p) => p.theme.cards1Background};
  border: 1px solid ${(p) => p.theme.cards1Border};
  /* The design's 18px card corner (no token carries 18). */
  border-radius: 18px;
  box-shadow:
    0 7px 33px rgba(17, 17, 17, 0.1),
    0 2px 6px rgba(17, 17, 17, 0.1);
  z-index: 1000;
`;

// 50px header strip: centered PREVIEW label over the neutrals/alpha/b2
// wash, hairline below (Bridge_DocPreview 14662-144779).
export const PreviewHeader = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 50px;
  flex-shrink: 0;
  padding: 0 ${designVariables.ui.semantic.spacingm}px;
  background: ${designVariables.primitives.neutrals.alpha.b2};
  border-bottom: 1px solid ${(p) => p.theme.semanticBorderLight};
`;

export const PreviewTitle = styled.span`
  font-size: 14px;
  font-weight: 600;
  line-height: 1.2;
  color: ${(p) => p.theme.semanticTextLight};
`;

// The header's action buttons (Copy on the code previews, Delete on the
// schema and test previews) ride the right edge in a row and only show
// while the pointer is inside the card (14669-182478) — or while one holds
// focus, so keyboard users aren't locked out.
export const HeaderActionSlot = styled.div`
  position: absolute;
  right: ${designVariables.ui.semantic.spacingm}px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  gap: ${designVariables.ui.semantic.spacingxs}px;
  opacity: 0;

  ${PreviewCard}:hover &,
  &:focus-within {
    opacity: 1;
  }
`;

// The scrolling content region — the app's OverlayScrollbars own the
// overflow (both axes: tall content and unwrapped long lines), constrained
// by the card's max-height.
export const PreviewScroll = styled(OverlayScroll)`
  min-height: 0;
`;

/** A header action button that names itself: the lib's icon buttons drop
 *  aria-label, and labelling through a ref from the card only works if the
 *  button exists on the card's first render — these cards render null
 *  until their content lands, so the label is owned by the button. */
export function PreviewHeaderButton({
  icon,
  label,
  onClick,
}: {
  icon: Icon;
  label: string;
  onClick: () => void;
}) {
  const ref = useLabeledButton(label);
  return <ButtonSmallSubtleIcon ref={ref} icon={icon} onClick={onClick} />;
}
